import { Center, Code, Flex, Group, ScrollArea, Stack, Text, Timeline, Title } from '@mantine/core';
import { IconCircle } from '@tabler/icons-react';
import moment from 'moment';
import { useEffect, useState } from 'react';

import { DataChangeActionEnum, Declaration, DeclarationTrace, DeclarationTraceDataChange, EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

interface DeclarationViewAuditProps {
  declaration: Declaration;
}
interface SelectedTrace {
  index: number;
  userId: string;
  userName: string;
  dateTime: string;
  dataChanges: DeclarationTraceDataChange[];
}

export const DeclarationViewAudit: React.FC<DeclarationViewAuditProps> = ({ declaration }) => {
  const [declarationTrace, setDeclarationTrace] = useState<DeclarationTrace[] | []>([]);
  const [selectedTrace, setSelectedTrace] = useState<SelectedTrace | null>(null);

  useEffect(() => {
    if (declaration.trace?.declarationTrace.length > 0) {
      handleTraceChange(0);
    } else {
      setSelectedTrace(null);
    }
    setDeclarationTrace(declaration.trace?.declarationTrace.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()));
  }, [declaration.trace?.declarationTrace]);

  const handleTraceChange = (index: number) => {
    setSelectedTrace({
      ...declaration.trace?.declarationTrace[index],
      index,
    } as any);
  };

  const prettifyJson = (value?: string) => {
    if (!value) return '';

    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value; // Not valid JSON, display as-is
    }
  };

  if (!declaration.trace?.declarationTrace?.length) {
    return <Center style={{ height: '200px' }}>No changes traced</Center>;
  }

  return (
    <Flex gap={20} align="flex-start">
      <Stack gap="md">
        <ScrollArea h="calc(100vh - 550px)" type="auto">
          <Timeline active={selectedTrace?.index ?? -1} bulletSize={24} lineWidth={2} color="blue" miw={250}>
            {declarationTrace.map((trace, index) => (
              <Timeline.Item
                key={`${trace.dateTime.toString()}-${index}`}
                bullet={<IconCircle size={12} />}
                onClick={() => handleTraceChange(index)}
                title={
                  <Stack
                    fw={selectedTrace?.index === index ? 'bold' : 'normal'}
                    c={selectedTrace?.index === index ? 'blue' : 'inherit'}
                    style={{ cursor: 'pointer' }}
                    gap={1}
                  >
                    <Text size="sm">{moment(trace.dateTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
                    <Text size="sm">{trace.userName}</Text>
                  </Stack>
                }
              />
            ))}
          </Timeline>
        </ScrollArea>
      </Stack>

      {selectedTrace && (
        <Stack gap="xl">
          <Title order={4}>Trace details</Title>
          <Stack gap={8}>
            <Group gap="xs">
              <Text fw={500}>Date:</Text>
              <Text>{moment(selectedTrace.dateTime).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</Text>
            </Group>

            <Group gap="xs">
              <Text fw={500}>User: </Text>
              <Text>{selectedTrace.userName}</Text>
            </Group>

            <ScrollArea h="calc(100vh - 650px)" offsetScrollbars="y" w={800}>
              {selectedTrace.dataChanges.map((change, changeIdx) => (
                <Stack key={changeIdx} mt="md" mb="lg" ml="md">
                  {change.action === DataChangeActionEnum.REMOVED && (
                    <Stack align="top" gap={4}>
                      <Text fw={500}>Item removed at path: {change.fieldName}</Text>
                      <Code block>{prettifyJson(change.oldFieldValue)}</Code>
                    </Stack>
                  )}
                  {change.action === DataChangeActionEnum.ADDDED && (
                    <Stack align="top" gap={4}>
                      <Text fw={500}>Item added at path: {change.fieldName}</Text>
                      <Code block>{prettifyJson(change.newFieldValue)}</Code>
                    </Stack>
                  )}
                  {change.action === DataChangeActionEnum.CHANGED && (
                    <Stack align="top" gap={4}>
                      <Text fw={500}>Item changed at path: {change.fieldName}</Text>
                      <Group align="top">
                        <Text fw={500}>Old value</Text>
                        <Text>{change.oldFieldValue}</Text>
                      </Group>
                      <Group align="top">
                        <Text fw={500}>New value</Text>
                        <Text>{change.newFieldValue}</Text>
                      </Group>
                    </Stack>
                  )}
                  {!change.action && (
                    <Stack align="top" gap={4}>
                      <Group align="top">
                        <Text fw={500}>Path</Text>
                        <Text>{change.fieldName}</Text>
                      </Group>
                      <Group align="top">
                        <Text fw={500}>Old value</Text>
                        <Text>{change.oldFieldValue}</Text>
                      </Group>
                      <Group align="top">
                        <Text fw={500}>New value</Text>
                        <Text>{change.newFieldValue}</Text>
                      </Group>
                    </Stack>
                  )}
                </Stack>
              ))}
            </ScrollArea>
          </Stack>
        </Stack>
      )}
    </Flex>
  );
};
